import * as React from 'react';
import clsx from 'clsx';
import { makeStyles, useMediaQuery, Stepper, Step, StepLabel, Typography, Hidden, } from '@material-ui/core';
import { CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';
import { ElementDimensions } from '../../styles/ElementDimensions';
import ErrorDialog, { ErrorDialogProps } from '../common/ErrorDialog';
import PaymentTerms from './PaymentTerms';
import {useStripe, useElements, PaymentElement} from '@stripe/react-stripe-js';
import { StripePaymentProps } from './StripePayment'
import FullWidthButton, { FullWidthButtonSize } from '../common/Buttons/FullWidthButton';
import CustomStepIcon from '../common/CustomStepIcon';
import SplitButtons, { SplitButtonProps } from '../common/Buttons/SplitButtons';
import { String_PayCheck_BackButton, String_PayCheck_CompleteButton, String_PayCheck_ProcessingPayment, String_PayCheck_Step1Label, String_PayCheck_Step2Label, String_PayCheck_TotalWithTipLabel, String_System_ConnectionError, String_System_GeneralError } from '../../utilities/StringHelper';
import themePrimary from '../../styles/themePrimary';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import Loader from '../common/Loader';
import LoaderLandscapePayment from '../common/LoaderLandscapePayment';

const useStyles = makeStyles({
    details: {
        /*width: '90%',*/
        position: 'absolute',
        top: (ElementDimensions.appBarHeight + 20) + 'px',
        bottom: (ElementDimensions.stepperHeight + ElementDimensions.footerBarHeightPaymentScreen) + 'px',
        left: 0,
        right: 0,
        paddingLeft: ElementDimensions.margin20 + 'px',
        paddingRight: ElementDimensions.margin20 + 'px',
        overflowY: 'scroll',
        overflowX: 'hidden',
    },
    detailsLandscape: {
        position: 'absolute',
        top: ElementDimensions.margin10 + 'px',
        bottom: (ElementDimensions.footerBarHeightPaymentScreen + ElementDimensions.stepperHeight - 20) + 'px',
        left: 'calc(100vw *.3)',
        width: 'calc(100vw *.7)',
        overflowY: 'scroll',
        overflowX: 'hidden',
    },
    hidden: {
        visibility: 'hidden',
    },
    root: {
        textAlign: 'center',
        height: 'calc(100vh - ' + (ElementDimensions.appBarHeight + ElementDimensions.stepperHeight + ElementDimensions.footerBarHeightPaymentScreen) + 'px)',
    },
    rootLandscape: {
        textAlign: 'center',
        height: '150%',
    },    
    footerButtons: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        height: (ElementDimensions.footerBarHeightPaymentScreen + ElementDimensions.stepperHeight) + 'px',
        padding: ElementDimensions.padding10 + 'px ' + '0px ' + ElementDimensions.padding10 + 'px 0px',
        width: '100%',
        borderTop: '2px solid ' + themePrimary.palette.grey[200],
        backgroundColor: themePrimary.palette.primary.contrastText,
    },
    footerButtonsLandscape: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        left: 'calc(100vw *.3)',
        height: (ElementDimensions.footerBarHeightPaymentScreen + ElementDimensions.stepperHeight - 20) + 'px',
        padding: ElementDimensions.padding10 + 'px ' + '0px ' + ElementDimensions.padding10 + 'px 0px',
        width: 'calc(100vw * .7)',
        borderTop: '2px solid ' + themePrimary.palette.grey[200],
        backgroundColor: themePrimary.palette.primary.contrastText,
    },
    loadingContainer: {
        margin: ElementDimensions.margin20 + 'px',
        textAlign: 'center',
    },
    processPaymentText: {
        marginTop: '280px',
        textAlign: 'center',
    },
    stepper: {
        paddingTop: ElementDimensions.padding5 + 'px',
        paddingBottom: ElementDimensions.padding10 + 'px',
    },
    stepsContainer: {
        paddingBottom: ElementDimensions.padding10 + 'px',
        height: '100%',
    },
    totalContainer: {
        display: 'flex',
        textAlign: 'center',
        paddingLeft: ElementDimensions.padding20 + 'px',
        paddingRight: ElementDimensions.padding20 + 'px',
        borderBottom: props => '2px solid ' + themePrimary.palette.grey[200],
    },
    totalInnerContainer: {
        marginTop: 'auto',
        marginBottom: 'auto',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    totalText: {
        color: themePrimary.palette.secondary.contrastText,
    },
    totalTextAmount: {
        color: themePrimary.palette.secondary.contrastText,
    },
});

export default function StripeCheckout(props: StripePaymentProps) {
    const { total, paymentMethodAllowed, backHandler, errorHandler } = props;
    const isPortrait = true;
    const [clientSecret, setClientSecret] = React.useState<string | null>(null);
    const [paying, setPaying] = React.useState<boolean>(false);
    const [isLoaded, setIsLoaded] = React.useState<boolean>(false);
    const [errorDialogOpen, setErrorDialogOpen] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>(''); 
    const [enableCompleteButton, setEnableCompleteButton] = React.useState<boolean>(false);
    const checkOutState = React.useContext(CheckOutStateContext);
    const userState = React.useContext(UserStateContext);
    const classes = useStyles({ primary_main_color: checkOutState.state.skin.primary_main_color, 
        primary_light_color: checkOutState.state.skin.primary_light_color, primary_dark_color: checkOutState.state.skin.primary_dark_color });

    const rootEl = React.useRef(null);
    const steps = getSteps();
    const stripe = useStripe();
    const elements = useElements();

    const confirmPayment = async (event: any | null) => {
        if (elements !== null && stripe !== null) {
            setPaying(true);
            // @ts-ignore
            const {error} = await stripe!!.confirmPayment({
                // @ts-ignore
                elements,
                confirmParams: {
                    return_url: process.env.REACT_APP_BASE_URL + '/payment/stripecomplete',
                },
            });
            
            if (error) {
                // This point will only be reached if there is an immediate error when
                // confirming the payment. Show error to your customer (for example, payment
                // details incomplete)
                setPaying(false);
                setErrorMessage(error.message!!);
                setErrorDialogOpen(true);
            } else {
                // Your customer will be redirected to your `return_url`. For some payment
                // methods like iDEAL, your customer will be redirected to an intermediate
                // site first to authorize the payment, then redirected to the `return_url`.
            }
        }        
    }

    const handleErrorDialogClose = (event: React.MouseEvent<HTMLElement>) => {
        setErrorDialogOpen(false);
        errorHandler();
    }

    const errorDialogProps: ErrorDialogProps = {
        open: errorDialogOpen,
        message: errorMessage,
        tryAgain: false,
        onCloseHandler: handleErrorDialogClose,
    }

    const onPaymentElementReadyHandler: any = (event: Event) => {
        setIsLoaded(true);
    }

    const onPaymentElementChangeHandler: any = (event: Event) => {
        // @ts-ignore
        if (event.complete) {
            setEnableCompleteButton(true);
        }
        else {
            setEnableCompleteButton(false);
        }
    }

    const handleLeftButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        backHandler();
    }

    const handleRightButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        confirmPayment(null);
    }

    const splitButtonProps: SplitButtonProps = {
        leftLabel: String_PayCheck_BackButton.find(s => s.key === userState.state.locale)!!.value,
        rightLabel: String_PayCheck_CompleteButton.find(s => s.key === userState.state.locale)!!.value,
        leftDisabled: false,
        rightDisabled: !enableCompleteButton,
        showIcons: false,
        useForwardIcon: true,
        useBackIcon: true,
        onLeftClickHandler: handleLeftButtonClick,
        onRightClickHandler: handleRightButtonClick,
    }
    
    function getSteps() {
        return [String_PayCheck_Step1Label.find(s => s.key === userState.state.locale)!!.value, 
            String_PayCheck_Step2Label.find(s => s.key === userState.state.locale)!!.value];
    }

    return (
        <div ref={rootEl} 
            className={clsx({
                [classes.root]: isPortrait,
                [classes.rootLandscape]: !isPortrait,            
        })}>
            <div className={clsx(classes.totalContainer, {
                [classes.hidden]: paying,
            })}>
                <div className={classes.totalInnerContainer}>
                    <Typography variant="h6" className={classes.totalText}>
                        {String_PayCheck_TotalWithTipLabel.find(s => s.key === userState.state.locale)!!.value}
                    </Typography>

                    <Typography variant="h5" className={classes.totalTextAmount}>
                        {total}
                    </Typography>
                </div>
            </div>
            <div className={clsx({
                [classes.details]: isPortrait,
                [classes.detailsLandscape]: !isPortrait,            
                [classes.hidden]: paying,
            })}>
                
                <form onSubmit={confirmPayment}>
                    <PaymentElement 
                        onReady={onPaymentElementReadyHandler}
                        onChange={onPaymentElementChangeHandler}
                    />

                    {isLoaded &&
                        <PaymentTerms />
                    }
                </form>
            </div>            
            
            {paying &&
                <div>
                    <div className={classes.loadingContainer}>
                            
                        {paying && isPortrait &&
                            <Loader />                    
                        }

                        {paying && !isPortrait &&
                            <LoaderLandscapePayment isFullScreen={true}/>                          
                        }

                    </div>
                    <Typography variant="h6" className={classes.processPaymentText}>
                        {String_PayCheck_ProcessingPayment.find(s => s.key === userState.state.locale)!!.value}
                    </Typography>
                </div>    
            }

            <div className={clsx({
                [classes.footerButtons]: isPortrait,
                [classes.footerButtonsLandscape]: !isPortrait,            
                [classes.hidden]: paying,
            })}>
                {isPortrait && 
                    <Stepper activeStep={1} alternativeLabel className={classes.stepper}>
                        {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel StepIconComponent={CustomStepIcon}>{label}</StepLabel>
                        </Step>
                        ))}
                    </Stepper>
                }
                
                {!isPortrait &&
                    <Stepper activeStep={1} className={classes.stepper}>
                        {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel StepIconComponent={CustomStepIcon}>{label}</StepLabel>
                        </Step>
                        ))}
                    </Stepper>
                }                

                <SplitButtons
                    leftLabel={splitButtonProps.leftLabel}
                    rightLabel={splitButtonProps.rightLabel}
                    leftDisabled={splitButtonProps.leftDisabled}
                    rightDisabled={splitButtonProps.rightDisabled}
                    showIcons={splitButtonProps.showIcons}
                    useForwardIcon={splitButtonProps.useForwardIcon}
                    useBackIcon={splitButtonProps.useBackIcon}
                    onLeftClickHandler={splitButtonProps.onLeftClickHandler}
                    onRightClickHandler={splitButtonProps.onRightClickHandler} />
                
            </div>      

            <ErrorDialog
                open={errorDialogProps.open}
                message={errorDialogProps.message}
                tryAgain={errorDialogProps.tryAgain}
                onCloseHandler={errorDialogProps.onCloseHandler} />
        </div>
        
    );
}